import { Select } from '@allrouted/ui/Select';

/**
 * A timezone, chosen rather than typed.
 *
 * The API wants an IANA name, and "America/New_York" typed by hand is one
 * misplaced capital away from a territory whose every clock time is wrong. A
 * short list of the zones fleets actually run in, labelled the way people say
 * them, with the browser's own zone always included and offered first.
 */

const ZONES: { value: string; label: string }[] = [
  { value: 'America/New_York', label: 'Eastern (New York)' },
  { value: 'America/Chicago', label: 'Central (Chicago)' },
  { value: 'America/Denver', label: 'Mountain (Denver)' },
  { value: 'America/Phoenix', label: 'Arizona (Phoenix, no DST)' },
  { value: 'America/Los_Angeles', label: 'Pacific (Los Angeles)' },
  { value: 'America/Anchorage', label: 'Alaska (Anchorage)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii (Honolulu)' },
  { value: 'America/Indiana/Indianapolis', label: 'Eastern (Indianapolis)' },
  { value: 'America/Detroit', label: 'Eastern (Detroit)' },
  { value: 'America/Kentucky/Louisville', label: 'Eastern (Louisville)' },
  { value: 'America/Puerto_Rico', label: 'Atlantic (Puerto Rico)' },
  { value: 'America/Toronto', label: 'Eastern (Toronto)' },
  { value: 'America/Winnipeg', label: 'Central (Winnipeg)' },
  { value: 'America/Edmonton', label: 'Mountain (Edmonton)' },
  { value: 'America/Vancouver', label: 'Pacific (Vancouver)' },
  { value: 'America/Halifax', label: 'Atlantic (Halifax)' },
  { value: 'America/Mexico_City', label: 'Mexico City' },
  { value: 'Europe/London', label: 'London' },
  { value: 'Europe/Dublin', label: 'Dublin' },
  { value: 'Europe/Paris', label: 'Central Europe (Paris)' },
  { value: 'Europe/Berlin', label: 'Central Europe (Berlin)' },
  { value: 'Asia/Dubai', label: 'Gulf (Dubai)' },
  { value: 'Asia/Karachi', label: 'Pakistan (Karachi)' },
  { value: 'Asia/Kolkata', label: 'India (Kolkata)' },
  { value: 'Asia/Singapore', label: 'Singapore' },
  { value: 'Australia/Sydney', label: 'Sydney' },
  { value: 'Pacific/Auckland', label: 'Auckland' },
  { value: 'UTC', label: 'UTC' },
];

export function browserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York';
  } catch {
    return 'America/New_York';
  }
}

export function TimezoneSelect({ value, onChange }: { value: string; onChange: (tz: string) => void }) {
  const mine = browserTimezone();
  const known = ZONES.find((z) => z.value === mine);
  const options = [
    { value: mine, label: known ? known.label : mine.replace(/_/g, ' '), hint: 'Your timezone' },
    ...ZONES.filter((z) => z.value !== mine),
  ];
  /* A value set elsewhere (the API, an import) still shows, even if unlisted. */
  if (value && !options.some((o) => o.value === value)) options.push({ value, label: value.replace(/_/g, ' '), hint: '' });
  return <Select value={value} options={options} onChange={onChange} />;
}
